// src/pages/Publisher/Campaign/components/CampaignInsightsDialog.tsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  Box,
  Typography,
} from '@mui/material';
import { Chart, ArcElement, Tooltip, Legend } from 'chart.js';
import { useDispatch } from 'react-redux';
import { fetchCampaignById } from '@/store/Campaign';
import CardByFollower from './cardByFollower';
import CardByGender from './CardByGender';

Chart.register(ArcElement, Tooltip, Legend);

interface Metrics {
  cpe: string;
  cpd: string;
  total_budget: string;
  spent: number;
  engagements: number;
  likes: number;
  shares: number;
  comments: number;
  downloads: number;
  wishlists: number;
  followers: number;
  male: number;
  female: number;
}

interface CampaignInsightsDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  gamicTitle: string;
  dateRange: string;
  campaignName: string;
  metrics?: Metrics;
  id: string;
}

const CampaignInsightsDialog: React.FC<CampaignInsightsDialogProps> = ({
  open,
  onClose,
  title,
  gamicTitle,
  dateRange,
  campaignName,
  id,
}) => {
  const dispatch = useDispatch<any>();
  const [campaignData, setCamapaignData] = useState<Metrics>();

  React.useEffect(() => {
    if (id && open) {
      const data = async () => {
        const res = await dispatch(fetchCampaignById(id));
        setCamapaignData(res.payload.data[0]);
      };

      data();
    }
  }, [id, open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      // disable the theme’s maxWidth breakpoint
      maxWidth={false}
      // remove fullWidth so it won’t stretch to 100%
      fullWidth={false}
      PaperProps={{
        sx: {
          width: '550px',
          background: '#FAFAFA',
          borderRadius: '24px',
        },
      }}
    >
      <DialogTitle
        sx={{
          background: '#FFFFFF',
          textAlign: 'center',
          pt: '32px',
          pb: '32px',
          fontSize: '32px',
          fontWeight: 'bold',
        }}
      >
        {title}
      </DialogTitle>

      <DialogContent
        sx={{
          background: '#FAFAFA',
          px: '40px',
          overflowY: 'auto', // or "scroll"
          // WebKit browsers (Chrome, Safari)
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          // Firefox
          scrollbarWidth: 'none',
          // IE 10+
          msOverflowStyle: 'none',
        }}
      >
        <Box>
          {/* gamic title pill */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <Typography
              component='span'
              sx={{
                backgroundColor: '#FF0050',
                color: '#fff',
                px: 2,
                py: 0.5,
                borderRadius: '9999px',
                fontSize: '0.875rem',
              }}
            >
              {gamicTitle}
            </Typography>
          </Box>

          {/* date & campaign */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
            <Box>
              <Typography sx={{ color: '#515151', fontSize: '1rem' }}>
                {dateRange}
              </Typography>
              <Typography sx={{ fontWeight: 500 }}>Date Range</Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography sx={{ color: '#515151', fontSize: '1rem' }}>
                {campaignName}
              </Typography>
              <Typography sx={{ fontWeight: 500 }}>Campaign Name</Typography>
            </Box>
          </Box>

          <Divider sx={{ my: '10px' }} />

          {/* top metrics */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 2,
              mb: 4,
            }}
          >
            {[
              ['CPE', `$ ${campaignData?.cpe || 0}`],
              ['CPD', `$ ${campaignData?.cpd || 0}`],
              ['Total Budget', `$ ${campaignData?.total_budget || 0}`],
              ['Spent', `$ ${campaignData?.spent || 0}`],
            ].map(([label, value]) => (
              <Box key={label} sx={{ bgcolor: '#fff', borderRadius: 2, p: 2 }}>
                <Typography sx={{ color: '#515151', fontSize: '1.125rem' }}>
                  {label}
                </Typography>
                <Typography sx={{ fontWeight: 'bold' }}>{value}</Typography>
              </Box>
            ))}
          </Box>

          <Divider sx={{ my: '10px' }} />

          {/* analytics list */}
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 2 }}>
            Analytics
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 1,
              mb: 4,
            }}
          >
            {[
              ['Total', campaignData?.engagements],
              ['Likes', campaignData?.likes],
              ['Shares', campaignData?.shares],
              ['Comments', campaignData?.comments],
              ['Downloads', campaignData?.downloads],
              ['Wishlist', campaignData?.wishlists],
            ].map(([label, value]) => (
              <React.Fragment key={label}>
                <Typography sx={{ fontSize: '0.875rem' }}>{label}</Typography>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: '#FF0050',
                    textAlign: 'right',
                  }}
                >
                  {value}
                </Typography>
              </React.Fragment>
            ))}
          </Box>

          <Divider sx={{ my: '10px' }} />
        </Box>

        <Box>
        </Box>
        {/* cards carousel */}
        <div className='flex flex-col gap-6 mb-4'>
          <CardByFollower 
          engagements={campaignData?.engagements}
          followers={campaignData?.followers}
          />
          <CardByGender 
          engagements={campaignData?.engagements}
          male={campaignData?.male}
          female={campaignData?.female}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CampaignInsightsDialog;
